import * as Yup from "yup";
import { AnyObject, NumberSchema, Reference } from "yup";
import { JOURNEY_EMAIL_ERROR, isEmailFieldName, isValidJourneyEmail } from "./emailValidation";

// key - max, value - min
const minMarks: any = {
  "340": 260,
  "800": 200,
  "1600": 400,
  "36": 1,
  "528": 118,
  "9":  1,
  "120": 0,
  "160": 10,
  "90": 10,
}

const getCurrentYear = () => new Date().getFullYear();

type NullableNumberSchema = NumberSchema<
  number | null | undefined,
  AnyObject,
  undefined,
  ""
>;

const isBlankCoApplicantValue = (v: any) =>
  v === null || v === undefined || String(v).trim() === "";

// Yup.number() casts "" to NaN, which surfaces as a typeError instead of the
// required message. Mapping blanks to undefined lets .required() report properly.
const blankToUndefined = (value: any, originalValue: any) =>
  originalValue === "" || originalValue === null || originalValue === undefined
    ? undefined
    : value;

/**
 * Required-field rules for the co-applicant "Basic Details" tab
 * (basicCoApplicant{N}...).
 *
 * Shared so the tab validates identically wherever it is rendered: Loan Details
 * (1,5) and Co-Applicant Additional Details (3,1). (3,1) has no branch of its
 * own â€” it falls through to the generic loop at the bottom of
 * getValidationSchema, where every `.required()` is commented out, so this tab
 * had no required validation there at all.
 *
 * Covers only the fields that tab actually renders. Relationship with Student,
 * Last Name, Country of Birth and City of Birth are deliberately left to the
 * (1,5) branch, which handles them separately.
 *
 * @returns true when the field was handled (the caller should stop processing it).
 */
const applyCoApplicantBasicDetailsRules = (
  shape: Record<string, any>,
  name: string
): boolean => {
  const isBlank = isBlankCoApplicantValue;

  // How should we address you? / Preferred title (dropdown)
  if (/^basicCoApplicant\d+salutation$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-salutation",
      "Please select a preferred title.",
      (v) => !isBlank(v)
    );
    return true;
  }
  // Co-Applicant's First Name
  if (/^basicCoApplicant\d+FirstName$/.test(name)) {
    shape[name] = Yup.string()
      .trim()
      .required("Please enter the co-applicant's first name.");
    return true;
  }
  // Co-Applicant's Last Name
  if (/^basicCoApplicant\d+LastName$/.test(name)) {
    shape[name] = Yup.string()
      .trim()
      .required("Please enter the co-applicant's last name.");
    return true;
  }
  // Gender (dropdown)
  if (/^basicCoApplicant\d+Gender$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-gender",
      "Please select a gender.",
      (v) => !isBlank(v)
    );
    return true;
  }
  // Date of Birth (blank / invalid format)
  if (/^basicCoApplicant\d+dob$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-dob",
      "Please enter or select the co-applicant's date of birth.",
      (v) => {
        if (isBlank(v)) return false;
        const d = new Date(v as any);
        return !Number.isNaN(d.getTime());
      }
    );
    return true;
  }
  // PAN (blank / invalid format / not yet verified) - 10-digit PAN
  const panMatch = name.match(/^basicCoApplicant(\d+)PanNumber$/);
  if (panMatch) {
    const isPanVerifiedField = `basicCoApplicant${panMatch[1]}IsPanVerified`;
    const isValidPanFormat = (value: any) =>
      /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(value).trim().toUpperCase());

    shape[name] = Yup.string()
      .transform((value) => (value ? String(value).trim() : value))
      .test(
        "required-co-pan",
        "Please enter a valid 10-digit PAN number.",
        (value) => {
          if (isBlank(value)) return false;
          return isValidPanFormat(value);
        }
      )
      .test(
        "co-pan-verified",
        "PAN verification is pending. Please verify the PAN to continue.",
        function (value) {
          if (isBlank(value) || !isValidPanFormat(value)) return true;
          const isVerified = (this.parent as any)?.[isPanVerifiedField];
          return isVerified === true || isVerified === "true";
        }
      );
    return true;
  }
  // Co-Applicant's Mobile Number (blank / invalid format)
  if (/^basicCoApplicant\d+PhoneNumber$/.test(name)) {
    shape[name] = Yup.string()
      .required("Please enter a valid 10-digit mobile number.")
      .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit mobile number.");
    return true;
  }
  // Co-Applicant's Email ID (blank / invalid format)
  if (/^basicCoApplicant\d+Email$/.test(name)) {
    shape[name] = Yup.string()
      .required("Please enter a valid email address (e.g., name@example.com).")
      .email("Please enter a valid email address (e.g., name@example.com).");
    return true;
  }
  // Nationality (dropdown/autocomplete)
  if (/^basicCoApplicant\d+Nationality$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-nationality",
      "Please select Nationality",
      (v) => !isBlank(v)
    );
    return true;
  }
  // Marital Status (dropdown)
  if (/^basicCoApplicant\d+MaritalStatus$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-marital-status",
      "Please select Marital Status",
      (v) => !isBlank(v)
    );
    return true;
  }
  // "Do you want to consider income of the co-applicant for loan evaluation?"
  // Only required from co-applicant 2 onwards â€” co-applicant 1's income is
  // always considered, so both tabs hide basicCoApplicant1PayParty. Requiring it
  // there would block Save with no field on screen to fix.
  const payPartyMatch = name.match(/^basicCoApplicant(\d+)PayParty$/);
  if (payPartyMatch) {
    if (Number(payPartyMatch[1]) >= 2) {
      shape[name] = Yup.mixed().nullable().test(
        "required-co-pay-party",
        "Please select the co-applicant's income should be considered for loan evaluation.",
        (v) => !isBlank(v)
      );
    } else {
      shape[name] = Yup.mixed().nullable().optional();
    }
    return true;
  }

  return false;
};

/**
 * Required-field rules for the co-applicant "Address Details" tab
 * (addressCoApplicant{N}...).
 *
 * Shared between Loan Details (1,5) and Co-Applicant Additional Details (3,1).
 * The current-address fields mirror the permanent address when
 * "currentsameAsPermanent" is checked, so they are not required then.
 *
 * @returns true when the field was handled (the caller should stop processing it).
 */
const applyCoApplicantAddressRules = (
  shape: Record<string, any>,
  name: string
): boolean => {
  const isBlank = isBlankCoApplicantValue;
  const isTrueLike = (v: any) => v === true || v === "true";

  const addrMatch = name.match(/^addressCoApplicant(\d+)(.+)$/);
  if (!addrMatch) return false;

  const num = addrMatch[1];
  const suffix = addrMatch[2];
  const sameAsPermanentField = `addressCoApplicant${num}currentsameAsPermanent`;

  // Permanent Address ---------------------------------------------
  if (suffix === "permanentOwnershipStatus") {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-perm-ownership",
      "Please select an ownership status.",
      (v) => !isBlank(v)
    );
    return true;
  }
  // The two "years" fields also carry the 0-150 range check. On (3,1) the generic
  // loop at the bottom of getValidationSchema supplies that range (but no
  // required), and these shared rules are applied after it — so the range is
  // folded in here to avoid dropping it.
  if (suffix === "permanentYearsInCurrentAddress") {
    shape[name] = Yup.number()
      .transform(blankToUndefined)
      .typeError("Please specify how long you have been living at this address.")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150")
      .required("Please specify how long you have been living at this address.");
    return true;
  }
  if (suffix === "permanentYearsInCurrentCity") {
    shape[name] = Yup.number()
      .transform(blankToUndefined)
      .typeError("Please specify how long you have lived in this city.")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150")
      .required("Please specify how long you have lived in this city.");
    return true;
  }
  if (suffix === "permanentAddressLine1") {
    shape[name] = Yup.string()
      .trim()
      .required("Please enter address details in Address Line 1.");
    return true;
  }
  // Address Line 2 & 3 are optional (permanent and current)
  if (
    suffix === "permanentAddressLine2" ||
    suffix === "permanentAddressLine3" ||
    suffix === "currentAddressLine2" ||
    suffix === "currentAddressLine3"
  ) {
    shape[name] = Yup.string().nullable().optional();
    return true;
  }
  if (suffix === "permanentPincode") {
    shape[name] = Yup.string()
      .required("Please enter a valid 6-digit pincode.")
      .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pincode.");
    return true;
  }
  if (suffix === "permanentCity") {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-perm-city",
      "Please enter or select the city.",
      (v) => !isBlank(v)
    );
    return true;
  }
  if (suffix === "permanentState") {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-perm-state",
      "Please enter or select the state.",
      (v) => !isBlank(v)
    );
    return true;
  }
  if (suffix === "permanentCountry") {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-perm-country",
      "Please enter or select the country.",
      (v) => !isBlank(v)
    );
    return true;
  }

  // Current Address (skipped when same as permanent) --------------
  if (suffix === "currentOwnershipStatus") {
    shape[name] = Yup.mixed().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) =>
        schema.test(
          "required-co-curr-ownership",
          "Please select an ownership status for your current address.",
          (v) => !isBlank(v)
        ),
    });
    return true;
  }
  if (suffix === "currentYearsInCurrentAddress") {
    shape[name] = Yup.number()
      .transform(blankToUndefined)
      .typeError(
        "Please specify how long you have been living at your current address."
      )
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150")
      .when(sameAsPermanentField, {
        is: (val: any) => isTrueLike(val),
        then: (schema) => schema.nullable().optional(),
        otherwise: (schema) =>
          schema.required(
            "Please specify how long you have been living at your current address."
          ),
      });
    return true;
  }
  if (suffix === "currentYearsInCurrentCity") {
    shape[name] = Yup.number()
      .transform(blankToUndefined)
      .typeError("Please specify how long you have lived in your current city.")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150")
      .when(sameAsPermanentField, {
        is: (val: any) => isTrueLike(val),
        then: (schema) => schema.nullable().optional(),
        otherwise: (schema) =>
          schema.required(
            "Please specify how long you have lived in your current city."
          ),
      });
    return true;
  }
  if (suffix === "currentAddressLine1") {
    shape[name] = Yup.string().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: () =>
        Yup.string()
          .trim()
          .required("Please enter current address details in Address Line 1."),
    });
    return true;
  }
  if (suffix === "currentPincode") {
    shape[name] = Yup.string().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: () =>
        Yup.string()
          .required(
            "Please enter a valid 6-digit pincode for your current address."
          )
          .matches(
            /^[0-9]{6}$/,
            "Please enter a valid 6-digit pincode for your current address."
          ),
    });
    return true;
  }
  if (suffix === "currentCity") {
    shape[name] = Yup.mixed().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) =>
        schema.test(
          "required-co-curr-city",
          "Please enter or select your current city.",
          (v) => !isBlank(v)
        ),
    });
    return true;
  }
  if (suffix === "currentState") {
    shape[name] = Yup.mixed().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) =>
        schema.test(
          "required-co-curr-state",
          "Please enter or select your current state.",
          (v) => !isBlank(v)
        ),
    });
    return true;
  }
  if (suffix === "currentCountry") {
    shape[name] = Yup.mixed().when(sameAsPermanentField, {
      is: (val: any) => isTrueLike(val),
      then: (schema) => schema.nullable().optional(),
      otherwise: (schema) =>
        schema.test(
          "required-co-curr-country",
          "Please enter or select your current country.",
          (v) => !isBlank(v)
        ),
    });
    return true;
  }

  // Any other addressCoApplicant field (e.g. the sameAsPermanent flag itself,
  // SameAs, Landmark) carries no rule â€” reported as handled so the caller does
  // not fall through to weaker generic handling for this prefix.
  return true;
};

/**
 * Required-field rules for the co-applicant "Employment Details" tab
 * (employmentCoApplicant{N}...).
 *
 * Shared between Loan Details (1,5) and Co-Applicant Additional Details (3,1).
 * Which fields are required depends on both "Are you employed?" and the selected
 * employment type, mirroring the render logic in the co-applicant sections:
 *   - isEmployed !== "Yes"             -> only isEmployed is required
 *   - employmentType Others/Farmer     -> only employmentType, monthlyObligation
 *                                         and monthlyIncome are shown/required
 *   - employmentType Salaried/SEP/SENP -> the full form incl. company address
 *                                         (address lines, landmark, pincode,
 *                                         city, state) is required
 * companyCountry stays optional (the company-address mandatory helper in the UI
 * does not cover it).
 *
 * `resolveIsEmployedFieldName` is injected because the radio is named
 * `...isEmployed` on some steps and `...employmentisEmployed` on others; pointing
 * a .when() at the wrong key would silently treat the co-applicant as not
 * employed and skip every sub-field rule.
 *
 * @returns true when the field was handled (the caller should stop processing it).
 */
const applyCoApplicantEmploymentRules = (
  shape: Record<string, any>,
  name: string,
  resolveIsEmployedFieldName: (empNum: string) => string
): boolean => {
  const isBlank = isBlankCoApplicantValue;
  const isEmployedYes = (v: any) =>
    v === true || String(v ?? "").toLowerCase() === "yes";

  // Are You Currently Employed? (radio) - required selection
  if (/^employmentCoApplicant\d+(?:employment)?isEmployed$/.test(name)) {
    shape[name] = Yup.mixed().nullable().test(
      "required-co-is-employed",
      "Please select the co-applicant's current employment status.",
      (v) => !isBlank(v)
    );
    return true;
  }

  const empMatch = name.match(/^employmentCoApplicant(\d+)(.+)$/);
  if (!empMatch) return false;

  const empNum = empMatch[1];
  const suffix = empMatch[2];
  const isEmployedField = resolveIsEmployedFieldName(empNum);
  const employmentTypeField = `employmentCoApplicant${empNum}employmentType`;

  const isOthersOrFarmer = (v: any) =>
    ["farmer", "others"].includes(String(v ?? "").toLowerCase());
  const isCompanyAddressType = (v: any) =>
    ["salaried", "sep", "senp"].includes(String(v ?? "").toLowerCase());

  // Employment type - required whenever employed = Yes
  if (suffix === "employmentType") {
    shape[name] = Yup.mixed().when(isEmployedField, {
      is: (val: any) => isEmployedYes(val),
      then: (schema) =>
        schema.test(
          "required-co-employment-type",
          "Please select an employment type.",
          (v) => !isBlank(v)
        ),
      otherwise: (schema) => schema.nullable().optional(),
    });
    return true;
  }

  // Monthly Obligation - required for every employment type when employed
  if (suffix === "monthlyObligation") {
    shape[name] = Yup.mixed().when(isEmployedField, {
      is: (val: any) => isEmployedYes(val),
      then: (schema) =>
        schema.test(
          "required-co-monthly-obligation",
          "Please enter the monthly obligation.",
          (v) => !isBlank(v)
        ),
      otherwise: (schema) => schema.nullable().optional(),
    });
    return true;
  }

  // Monthly Income - required for every employment type when employed
  if (suffix === "monthlyIncome") {
    shape[name] = Yup.mixed().when(isEmployedField, {
      is: (val: any) => isEmployedYes(val),
      then: (schema) =>
        schema.test(
          "required-co-monthly-income",
          "Please enter the monthly income.",
          (v) => !isBlank(v)
        ),
      otherwise: (schema) => schema.nullable().optional(),
    });
    return true;
  }

  // Company Name / Designation / Year / Month - required when employed and
  // employment type is NOT Others/Farmer (those hide these fields).
  if (
    suffix === "companyName" ||
    suffix === "designation" ||
    suffix === "employmentYear" ||
    suffix === "employmentMonth"
  ) {
    const messages: Record<string, string> = {
      companyName: "Please enter the company name.",
      designation: "Please select the designation.",
      employmentYear: "Please enter total experience years.",
      employmentMonth: "Please enter total experience months.",
    };
    shape[name] = Yup.mixed().when([isEmployedField, employmentTypeField], {
      is: (isEmployed: any, employmentType: any) =>
        isEmployedYes(isEmployed) && !isOthersOrFarmer(employmentType),
      then: (schema) =>
        schema.test(`required-co-${suffix}`, messages[suffix], (v) => !isBlank(v)),
      otherwise: (schema) => schema.nullable().optional(),
    });
    return true;
  }

  // Company Address fields - required only when employed and employment type is
  // Salaried/SEP/SENP (mirrors withMandatoryCompanyAddress).
  // companyCountry is intentionally left optional.
  if (
    suffix === "companyAddress1" ||
    suffix === "companyAddress2" ||
    suffix === "companyAddress3" ||
    suffix === "landmark" ||
    suffix === "pincode" ||
    suffix === "city" ||
    suffix === "state"
  ) {
    const messages: Record<string, string> = {
      companyAddress1: "Please enter the company address in Address Line 1.",
      companyAddress2: "Please enter the company address in Address Line 2.",
      companyAddress3: "Please enter the company address in Address Line 3.",
      landmark: "Please enter a nearby landmark for the company address.",
      pincode: "Please enter a valid 6-digit company pincode.",
      city: "Please enter or select the company city.",
      state: "Please enter or select the company state.",
    };
    shape[name] = Yup.mixed().when([isEmployedField, employmentTypeField], {
      is: (isEmployed: any, employmentType: any) =>
        isEmployedYes(isEmployed) && isCompanyAddressType(employmentType),
      then: (schema) =>
        schema.test(`required-co-${suffix}`, messages[suffix], (v) => {
          if (isBlank(v)) return false;
          if (suffix === "pincode") {
            return /^[0-9]{6}$/.test(String(v).trim());
          }
          return true;
        }),
      otherwise: (schema) => schema.nullable().optional(),
    });
    return true;
  }

  // Everything else on the employment section (companyCountry and any
  // verification/hidden flags) is optional.
  return true;
};

const buildValidationSchema = (
  fields: any[],
  activeHorizontalIndex?: number,
  activeVerticalIndex?: number,
  notInList?: any,
  pageType?: any
) => {
  const sanitizedFields = (Array.isArray(fields) ? fields : [])
    .map((field) => ({
      ...field,
      name: field && field.name != null ? String(field.name).trim() : "",
    }))
    .filter((field) => field.name !== "");

  fields = sanitizedFields;
  const shape: Record<string, any> = {};


  // Utility function to determine the reference point for sequential validation
  const getRefFor12th = (
    currentType: string | null | undefined,
    previousCourseRef: string
  ) => {
    if (currentType === "12th") {
      return {
        ref: Yup.ref("course1Year") as Reference<number>,
        messageSuffix: "10th Year",
      };
    }
    return {
      ref: Yup.ref(previousCourseRef) as Reference<number>,
      messageSuffix: "Previous Course",
    };
  };

  // if (activeHorizontalIndex === 1 && activeVerticalIndex === 1) {
  //   fields.forEach((field) => {
  //     if (field.name == "permanentAddressLine1" || field.name == "currentAddressLine1") {

  //     }
  //   });
  // }

  // The co-applicant "Are you employed?" radio is registered as
  // `employmentCoApplicant{N}isEmployed` on some steps and
  // `employmentCoApplicant{N}employmentisEmployed` on others. Resolve it from the
  // real field list so the employment rules' .when() dependencies point at the
  // key that actually holds the answer â€” aimed at a missing key, every
  // sub-field would read as "not employed" and silently skip validation.
  const resolveCoApplicantIsEmployedField = (empNum: string): string => {
    const matched = fields.find((f: any) =>
      new RegExp(
        `^employmentCoApplicant${empNum}(?:employment)?isEmployed$`
      ).test(String(f?.name || ""))
    );
    return matched?.name || `employmentCoApplicant${empNum}isEmployed`;
  };

  // Step-aware validation
  // PRELOGIN: Verify Yourself and Provide Consent (horizontal 0, vertical 0)
  if (activeHorizontalIndex === 0 && activeVerticalIndex === 0) {
    console.log("DEBUG: Setting up validation for prelogin page, fields:", fields.map(f => ({ name: f.name, type: f.type })));
    // Specific messages required by acceptance criteria
    fields.forEach((field) => {
      if (field.name === "product") {
        shape[field.name] = Yup.string().notOneOf([""], "Please select a product to proceed with your application.");
        return;
      }
      if (field.name === "applicantType") {
        shape[field.name] = Yup.string().notOneOf([""], "Please select whether you are applying as a Student or a Co-applicant.");
        return;
      }
      if (field.name === "firstName") {
        shape[field.name] = Yup.string().trim().required("Please enter the student's first name.");
        return;
      }
      if (field.name === "lastName") {
        shape[field.name] = Yup.string().trim().required("Please enter the student's last name.");
        return;
      }
      if (field.name === "phone_number") {
        shape[field.name] = Yup.string().matches(/^[0-9]{10}$/, "Please enter a valid 10-digit mobile number.").required("Please enter a valid 10-digit mobile number.");
        return;
      }
      if (field.name === "email") {
        shape[field.name] = Yup.string().email("Please enter a valid email address (e.g., name@example.com)." ).required("Please enter a valid email address (e.g., name@example.com).");
        return;
      }
      if (field.name === "pincode") {
        shape[field.name] = Yup.string().matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pincode.").required("Please enter a valid 6-digit pincode.");
        return;
      }
      if (field.name === "terms_conditions" && field.required) {
        shape[field.name] = Yup.boolean().oneOf([true], "Consent is required to continue.");
        return;
      }
      if (field.name === "privacy_policy" && field.required) {
        shape[field.name] = Yup.boolean().oneOf([true], "Consent is required to continue.");
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // APPLICANT & LOAN: Student Details (horizontal 1, vertical 0)
  if (activeHorizontalIndex === 1 && activeVerticalIndex === 0) {
    const isBlank = (v: any) =>
      v === null || v === undefined || String(v).trim() === "";

    shape.pan = Yup.string()
      .transform((value) => (value ? String(value).trim() : value))
      .test(
        "pan-selection-and-format",
        "Please select whether you have a PAN card.",
        function (value) {
          const { createError } = this;
          // Not selected yet
          if (isBlank(value)) {
            return createError({
              message: "Please select whether you have a PAN card.",
            });
          }
          // User selected "No" - PAN not required
          if (String(value).trim().toLowerCase() === "no") {
            return true;
          }
          // User selected "Yes" - must be a valid 10-digit PAN
          const pan = String(value).trim().toUpperCase();
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) {
            return createError({
              message: "Please enter a valid 10-digit PAN number.",
            });
          }
          return true;
        }
      )
      .test(
        "pan-verified",
        "PAN verification is pending. Please verify the PAN to continue.",
        function (value) {
          const raw = String(value ?? "").trim();
          if (!raw || raw.toLowerCase() === "no") return true;
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(raw.toUpperCase())) return true;
          const isPanVerified = (this.parent as any)?.isPanVerified;
          return isPanVerified === true || isPanVerified === "true";
        }
      );

    // The loan summary hides the Aadhaar field on this step (FormEdit's
    // hideAadhaarOnSummaryPanStep), so requiring its verification there would
    // block Save with an error the user can neither see nor fix.
    const isSummaryPage = pageType === "SUMMARY";
    shape.aadhaar = Yup.mixed().when(
      ["pan", "isPanVerified", "isAadhaarVerified"],
      {
        is: (pan: any, isPanVerified: any, isAadhaarVerified: any) => {
          if (isSummaryPage) return false;
          const isTrue = (v: any) => v === true || v === "true";
          const hasNoPan = String(pan ?? "").trim().toLowerCase() === "no";
          const aadhaarSectionShown = hasNoPan || isTrue(isPanVerified);
          return aadhaarSectionShown && !isTrue(isAadhaarVerified);
        },
        then: (schema) =>
          schema.test(
            "required-aadhaar-verification",
            "Please verify your Aadhaar through DigiLocker to continue.",
            () => false
          ),
        otherwise: (schema) => schema.nullable().optional(),
      }
    );

    fields.forEach((field) => {
      // Preferred Title (dropdown)
      if (field.name === "salutation") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-salutation",
          "Please select a preferred title.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Student First Name
      if (field.name === "firstName" || field.name === "studentDetailsFirstName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter the student's first name.");
        return;
      }
      // Student Last Name
      if (field.name === "lastName" || field.name === "studentDetailsLastName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter the student's last name.");
        return;
      }
      // Student Mobile Number
      if (field.name === "phone_number") {
        shape[field.name] = Yup.string()
          .matches(/^[0-9]{10}$/, "Please enter a valid 10-digit mobile number.")
          .required("Please enter a valid 10-digit mobile number.");
        return;
      }
      // Student Email ID + OTP verification
      if (field.name === "email") {
        shape[field.name] = Yup.string()
          .email("Please enter a valid email address (e.g., name@example.com).")
          .required("Please enter a valid email address (e.g., name@example.com).")
          .test(
            "email-otp-verified",
            "Email verification is pending. Please enter the OTP to verify.",
            function (value) {
              // Only enforce verification once a valid email has been entered
              if (isBlank(value)) return true;
              return this.parent?.isEmailVerified === true;
            }
          );
        return;
      }
      // Date of Birth
      if (field.name === "dob") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-dob",
          "Please enter or select the student's date of birth.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Gender (dropdown)
      if (field.name === "gender") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-gender",
          "Please select a gender.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Father First Name
      if (field.name === "fatherFirstName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter your father's first name.");
        return;
      }
      // Father Last Name
      if (field.name === "fatherLastName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter your father's last name.");
        return;
      }
      // Mother First Name
      if (field.name === "motherFirstName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter your mother's first name.");
        return;
      }
      // Mother Last Name
      if (field.name === "motherLastName") {
        shape[field.name] = Yup.string()
          .trim()
          .required("Please enter your mother's last name.");
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // APPLICANT & LOAN: Address Details (horizontal 1, vertical 1)
  if (activeHorizontalIndex === 1 && activeVerticalIndex === 1) {
    const isBlank = (v: any) =>
      v === null || v === undefined || String(v).trim() === "";

    // When "Keep Current Address same as Permanent Address" is checked, the
    // current-address fields are hidden and mirrored from the permanent
    // address, so they should not be validated as required.
    const requiredWhenCurrentNotSameAsPermanent = (
      builder: (schema: Yup.StringSchema) => Yup.StringSchema
    ) =>
      Yup.string().when("currentsameAsPermanent", {
        is: (val: any) => val === true || val === "true",
        then: (schema) => schema.nullable().optional(),
        otherwise: () => builder(Yup.string()),
      });

    fields.forEach((field) => {
      // Ownership status (dropdown) - permanent
      if (field.name === "permanentOwnershipStatus") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-permanent-ownership-status",
          "Please select an ownership status.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Ownership status (dropdown) - current (only when not same as permanent)
      if (field.name === "currentOwnershipStatus") {
        shape[field.name] = Yup.mixed().when("currentsameAsPermanent", {
          is: (val: any) => val === true || val === "true",
          then: (schema) => schema.nullable().optional(),
          otherwise: (schema) =>
            schema.test(
              "required-current-ownership-status",
              "Please select an ownership status.",
              (v) => !isBlank(v)
            ),
        });
        return;
      }

      // How long have you been living here? - permanent
      if (field.name === "permanentYearsInCurrentAddress") {
        shape[field.name] = Yup.number()
          .typeError("Please specify how long you have been living at this address.")
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .required("Please specify how long you have been living at this address.")
          .min(0, "Years must be at least 0")
          .max(150, "Years cannot exceed 150");
        return;
      }
      // How long have you lived in this city? - permanent
      if (field.name === "permanentYearsInCurrentCity") {
        shape[field.name] = Yup.number()
          .typeError("Please specify how long you have lived in this city.")
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .required("Please specify how long you have lived in this city.")
          .min(0, "Years must be at least 0")
          .max(150, "Years cannot exceed 150");
        return;
      }
      // How long have you been living here? - current (only when not same)
      if (field.name === "currentYearsInCurrentAddress") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .min(0, "Years must be at least 0")
          .max(150, "Years cannot exceed 150")
          .when("currentsameAsPermanent", {
            is: (val: any) => val === true || val === "true",
            then: (schema) => schema.nullable().optional(),
            otherwise: (schema) =>
              schema
                .typeError("Please specify how long you have been living at this address.")
                .required("Please specify how long you have been living at this address."),
          });
        return;
      }
      // How long have you lived in this city? - current (only when not same)
      if (field.name === "currentYearsInCurrentCity") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .min(0, "Years must be at least 0")
          .max(150, "Years cannot exceed 150")
          .when("currentsameAsPermanent", {
            is: (val: any) => val === true || val === "true",
            then: (schema) => schema.nullable().optional(),
            otherwise: (schema) =>
              schema
                .typeError("Please specify how long you have lived in this city.")
                .required("Please specify how long you have lived in this city."),
          });
        return;
      }

      // Address Line 1 - permanent (required, max 50)
      if (field.name === "permanentAddressLine1") {
        shape[field.name] = Yup.string()
          .trim()
          .max(50, "Maximum 50 characters allowed.")
          .required("Please enter address details in Address Line 1.");
        return;
      }
      // Address Line 1 - current (required only when not same as permanent)
      if (field.name === "currentAddressLine1") {
        shape[field.name] = requiredWhenCurrentNotSameAsPermanent((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter address details in Address Line 1.")
        );
        return;
      }
      // Address Line 2 & 3 - optional (max 50) for both permanent and current
      if (
        field.name === "permanentAddressLine2" ||
        field.name === "permanentAddressLine3" ||
        field.name === "currentAddressLine2" ||
        field.name === "currentAddressLine3"
      ) {
        shape[field.name] = Yup.string()
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      // Pincode - permanent (required, 6-digit)
      if (field.name === "permanentPincode") {
        shape[field.name] = Yup.string()
          .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pincode.")
          .required("Please enter a valid 6-digit pincode.");
        return;
      }
      // Pincode - current (required only when not same as permanent)
      if (field.name === "currentPincode") {
        shape[field.name] = requiredWhenCurrentNotSameAsPermanent((schema) =>
          schema
            .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pincode.")
            .required("Please enter a valid 6-digit pincode.")
        );
        return;
      }

      // City - permanent (required)
      if (field.name === "permanentCity") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-permanent-city",
          "Please enter or select your city.",
          (v) => !isBlank(v)
        );
        return;
      }
      // City - current
      if (field.name === "currentCity") {
        shape[field.name] = Yup.mixed().when("currentsameAsPermanent", {
          is: (val: any) => val === true || val === "true",
          then: (schema) => schema.nullable().optional(),
          otherwise: (schema) =>
            schema.test(
              "required-current-city",
              "Please enter or select your city.",
              (v) => !isBlank(v)
            ),
        });
        return;
      }

      // State - permanent (required)
      if (field.name === "permanentState") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-permanent-state",
          "Please enter or select your state.",
          (v) => !isBlank(v)
        );
        return;
      }
      // State - current
      if (field.name === "currentState") {
        shape[field.name] = Yup.mixed().when("currentsameAsPermanent", {
          is: (val: any) => val === true || val === "true",
          then: (schema) => schema.nullable().optional(),
          otherwise: (schema) =>
            schema.test(
              "required-current-state",
              "Please enter or select your state.",
              (v) => !isBlank(v)
            ),
        });
        return;
      }

      // Country - permanent (required)
      if (field.name === "permanentCountry") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-permanent-country",
          "Please enter or select your country.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Country - current
      if (field.name === "currentCountry") {
        shape[field.name] = Yup.mixed().when("currentsameAsPermanent", {
          is: (val: any) => val === true || val === "true",
          then: (schema) => schema.nullable().optional(),
          otherwise: (schema) =>
            schema.test(
              "required-current-country",
              "Please enter or select your country.",
              (v) => !isBlank(v)
            ),
        });
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // APPLICANT & LOAN: Employment Details (horizontal 1, vertical 2)
  if (activeHorizontalIndex === 1 && activeVerticalIndex === 2) {
    const isBlank = (v: any) =>
      v === null || v === undefined || String(v).trim() === "";

    // Employment fields are only relevant when the applicant is employed
    // (employmentisEmployed === "Yes"). When "No" is chosen, all other
    // employment fields are cleared/hidden and should not block submission.
    const isEmployedYes = (v: any) =>
      v === true || String(v ?? "").toLowerCase() === "yes";

    // Year / Month / Designation are hidden (not required) when the
    // employment type is "Farmer" or "Others".
    const isFarmerOrOthers = (v: any) =>
      ["farmer", "others"].includes(String(v ?? "").toLowerCase());

    // Required when employed. `builder` produces the "employed" branch schema.
    const requiredWhenEmployed = (
      builder: (schema: Yup.StringSchema) => Yup.StringSchema
    ) =>
      Yup.string().when("employmentisEmployed", {
        is: (val: any) => isEmployedYes(val),
        then: () => builder(Yup.string()),
        otherwise: (schema) => schema.nullable().optional(),
      });

    // Required only when employed AND the employment type is NOT Farmer/Others.
    // For "Farmer"/"Others" only Monthly Financial Commitments (obligation)
    // and Monthly Income stay required â€” company name/address/landmark/pincode
    // all become optional.
    const requiredWhenEmployedAndNotFarmerOrOthers = (
      builder: (schema: Yup.StringSchema) => Yup.StringSchema
    ) =>
      Yup.string().when(["employmentisEmployed", "employmentType"], {
        is: (isEmployed: any, employmentType: any) =>
          isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
        then: () => builder(Yup.string()),
        otherwise: (schema) => schema.nullable().optional(),
      });

    fields.forEach((field) => {
      // Are you currently Employed? (radio)
      if (field.name === "employmentisEmployed") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-is-employed",
          "Please select whether you are currently employed.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Employment type (dropdown)
      if (field.name === "employmentType") {
        shape[field.name] = Yup.mixed().when("employmentisEmployed", {
          is: (val: any) => isEmployedYes(val),
          then: (schema) =>
            schema.test(
              "required-employment-type",
              "Please select an employment type.",
              (v) => !isBlank(v)
            ),
          otherwise: (schema) => schema.nullable().optional(),
        });
        return;
      }

      // Monthly Financial Commitments (monthly obligation)
      if (field.name === "employmentMonthlyObligation") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .min(0, "Monthly Obligation cannot be negative")
          .when("employmentisEmployed", {
            is: (val: any) => isEmployedYes(val),
            then: (schema) =>
              schema
                .typeError("Please enter your monthly financial commitments.")
                .required("Please enter your monthly financial commitments."),
            otherwise: (schema) => schema.nullable().optional(),
          });
        return;
      }

      // Company Name - not required for Farmer/Others
      if (field.name === "employmentCompanyName") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter your company name.")
        );
        return;
      }

      // Designation (dropdown) - not required for Farmer/Others
      if (field.name === "employmentDesignation") {
        shape[field.name] = Yup.mixed().when(
          ["employmentisEmployed", "employmentType"],
          {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema.test(
                "required-employment-designation",
                "Please select your designation.",
                (v) => !isBlank(v)
              ),
            otherwise: (schema) => schema.nullable().optional(),
          }
        );
        return;
      }

      // Monthly Income
      if (field.name === "employmentMonthlyIncome") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .when("employmentisEmployed", {
            is: (val: any) => isEmployedYes(val),
            then: (schema) =>
              schema
                .typeError("Please enter your monthly income.")
                .required("Please enter your monthly income."),
            otherwise: (schema) => schema.nullable().optional(),
          });
        return;
      }

      // Total years - not required for Farmer/Others
      if (field.name === "employmentYear") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .min(0, "Years cannot be negative")
          .max(60, "Employment year cannot be greater than 60")
          .integer("Must be a whole number")
          .when(["employmentisEmployed", "employmentType"], {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema
                .typeError("Please enter total experience years.")
                .required("Please enter total experience years."),
            otherwise: (schema) => schema.nullable().optional(),
          });
        return;
      }

      // Total Months - not required for Farmer/Others
      if (field.name === "employmentMonth") {
        shape[field.name] = Yup.number()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .min(0, "Months cannot be negative")
          .max(12, "Employment month cannot be greater than 12")
          .integer("Must be a whole number")
          .when(["employmentisEmployed", "employmentType"], {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema
                .typeError("Please enter total experience months.")
                .required("Please enter total experience months."),
            otherwise: (schema) => schema.nullable().optional(),
          });
        return;
      }

      // Company Address Line 1 - not required for Farmer/Others
      if (field.name === "employmentCompanyAddress1") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter your company address in Line 1.")
        );
        return;
      }
      // Company Address Line 2 - not required for Farmer/Others
      if (field.name === "employmentCompanyAddress2") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter your company address in Line 2.")
        );
        return;
      }
      // Company Address Line 3 - not required for Farmer/Others
      if (field.name === "employmentCompanyAddress3") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter your company address in Line 3.")
        );
        return;
      }

      // Landmark - not required for Farmer/Others
      if (field.name === "employmentLandmark") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .trim()
            .max(50, "Maximum 50 characters allowed.")
            .required("Please enter a nearby landmark for your company address.")
        );
        return;
      }

      // Pincode - not required for Farmer/Others
      if (field.name === "employmentCompanyPincode") {
        shape[field.name] = requiredWhenEmployedAndNotFarmerOrOthers((schema) =>
          schema
            .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit company pincode.")
            .required("Please enter a valid 6-digit company pincode.")
        );
        return;
      }

      // City - not required for Farmer/Others
      if (field.name === "employmentCompanyCity") {
        shape[field.name] = Yup.mixed().when(
          ["employmentisEmployed", "employmentType"],
          {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema.test(
                "required-employment-city",
                "Please enter or select the company city.",
                (v) => !isBlank(v)
              ),
            otherwise: (schema) => schema.nullable().optional(),
          }
        );
        return;
      }

      // State - not required for Farmer/Others
      if (field.name === "employmentCompanyState") {
        shape[field.name] = Yup.mixed().when(
          ["employmentisEmployed", "employmentType"],
          {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema.test(
                "required-employment-state",
                "Please enter or select the company state.",
                (v) => !isBlank(v)
              ),
            otherwise: (schema) => schema.nullable().optional(),
          }
        );
        return;
      }

      // Country - not required for Farmer/Others
      if (field.name === "employmentCompanyCountry") {
        shape[field.name] = Yup.mixed().when(
          ["employmentisEmployed", "employmentType"],
          {
            is: (isEmployed: any, employmentType: any) =>
              isEmployedYes(isEmployed) && !isFarmerOrOthers(employmentType),
            then: (schema) =>
              schema.test(
                "required-employment-country",
                "Please enter or select the company country.",
                (v) => !isBlank(v)
              ),
            otherwise: (schema) => schema.nullable().optional(),
          }
        );
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // PRELOGIN: Provide Course Details (horizontal 0, vertical 1)
  if (activeHorizontalIndex === 0 && activeVerticalIndex === 1) {
    fields.forEach((field) => {
      if (field.name === "courseCategory") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-course-category",
          "Please select a course category.",
          (v) => v !== null && v !== undefined && v !== ""
        );
        return;
      }
      if (field.name === "admissionStatus") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-admission-status",
          "Please select an admission status.",
          (v) => v !== null && v !== undefined && v !== ""
        );
        return;
      }
      if (field.name === "country") {
        shape[field.name] = Yup.mixed().nullable().test("required-country", "Please select a Study Destination.", (v) => v !== null && v !== undefined && v !== "");
        return;
      }
      if (field.name === "university") {
        shape[field.name] = Yup.mixed().nullable().test("required-university", "Please select a University Name.", (v) => v !== null && v !== undefined && v !== "");
        return;
      }
      if (field.name === "institute") {
        shape[field.name] = Yup.mixed().nullable().test("required-institute", "Please select an Institute Name.", (v) => v !== null && v !== undefined && v !== "");
        return;
      }
      if (field.name === "courseName") {
        shape[field.name] = Yup.mixed().nullable().test("required-course", "Please select a Course Name.", (v) => v !== null && v !== undefined && v !== "");
        return;
      }
      if (field.name === "courseType") {
        shape[field.name] = Yup.mixed().nullable().test(
          "required-course-type",
          "Please select a Course Type.",
          (v) => v !== null && v !== undefined && v !== ""
        );
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // PRELOGIN: Provide Loan Details (horizontal 0, vertical 2)
  if (activeHorizontalIndex === 0 && activeVerticalIndex === 2) {
    fields.forEach((field) => {
      if (field.name === "loanAmount" || field.name === "LoanDetailsLoanAmount") {
        shape[field.name] = Yup.number()
          .typeError("Please enter the required loan amount.")
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .required("Please enter the required loan amount.")
          .min(1000000, "Loan amount must be at least 10,00,000")
          .max(15000000, "Loan amount cannot exceed 1,50,00,000")
          .test(
            "loan-amount-not-greater-than-total-cost",
            "Loan amount cannot be greater than total cost",
            function (value) {
              const { totalCost } = this.parent;
              if (!totalCost || totalCost < 1000000 || totalCost > 50000000) {
                return true;
              }
              if (!value) return true;
              return value <= totalCost;
            }
          );
        return;
      }
      if (field.name === "totalCost") {
        shape[field.name] = Yup.number()
          .typeError("Please enter the total education cost.")
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .required("Please enter the total education cost.")
          .min(1000000, "Total cost must be at least 10,00,000")
          .max(50000000, "Total cost cannot exceed 5,00,00,000");
        return;
      }
    });

    return Yup.object().shape(shape);
  }

  // APPLICANT & LOAN: Co-Applicant Details (horizontal 1, vertical 5)
  // Validates the Co-applicant Personal (AC 3), Address (AC 4) and Employment
  // (AC 5) sections. Co-applicant fields are numbered per co-applicant using
  // the prefixes basicCoApplicant{N}, addressCoApplicant{N} and
  // employmentCoApplicant{N}, so rules are built for every co-applicant
  // number present in the fields list.
  if (activeHorizontalIndex === 1 && activeVerticalIndex === 5) {
    const isBlank = (v: any) =>
      v === null || v === undefined || String(v).trim() === "";

    const isTrueLike = (v: any) => v === true || v === "true";
    const isEmployedYes = (v: any) =>
      v === true || String(v ?? "").toLowerCase() === "yes";

    fields.forEach((field) => {
      const name: string = field.name;

      // ---- AC 3: Personal Details (basicCoApplicant{N}...) ----

      // Relationship with Student (dropdown)
      if (/^basicCoApplicant\d+RelationWithStudent$/.test(name)) {
        shape[name] = Yup.mixed().nullable().test(
          "required-co-relationship",
          "Please select your relationship with the student.",
          (v) => !isBlank(v)
        );
        return;
      }
      // Specify Relationship — required once Relationship with Student is "OTHERS".
      if (/^basicCoApplicant\d+(RelationValue|OtherRelationWithStudent)$/.test(name)) {
        const relationFieldName = name.replace(
          /(RelationValue|OtherRelationWithStudent)$/,
          "RelationWithStudent"
        );
        shape[name] = Yup.mixed().nullable().when(relationFieldName, {
          is: (relation: any) =>
            String(relation?.value ?? relation ?? "").toUpperCase() === "OTHERS",
          then: (schema) =>
            schema.test(
              "required-co-specify-relationship",
              "Please specify your relationship with student",
              (v) => !isBlank(v)
            ),
        });
        return;
      }
      // Salutation, First Name, Gender, DOB, PAN, Mobile, Email, Nationality,
      // Marital Status and PayParty come from the shared Basic Details rules, so
      // this tab validates identically here and on (3,1).
      if (applyCoApplicantBasicDetailsRules(shape, name)) {
        return;
      }
      // Co-Applicant's Last Name
      if (/^basicCoApplicant\d+LastName$/.test(name)) {
        shape[name] = Yup.string()
          .trim()
          .required("Please enter the co-applicant's last name.");
        return;
      }
      // Country of Birth (dropdown/autocomplete)
      if (/^basicCoApplicant\d+CountryOfBirth$/.test(name)) {
        shape[name] = Yup.mixed().nullable().test(
          "required-co-country-of-birth",
          "Please select Country of Birth",
          (v) => !isBlank(v)
        );
        return;
      }
      // City of Birth (dropdown/autocomplete)
      if (/^basicCoApplicant\d+CityOfBirth$/.test(name)) {
        shape[name] = Yup.mixed().nullable().test(
          "required-co-city-of-birth",
          "Please select City of Birth",
          (v) => !isBlank(v)
        );
        return;
      }
      // ---- AC 4: Address Details (addressCoApplicant{N}...) ----
      // Shared with (3,1) so both co-applicant Address tabs validate the same.
      if (applyCoApplicantAddressRules(shape, name)) {
        return;
      }

      // ---- AC 5: Employment Details (employmentCoApplicant{N}...) ----
      // Shared with (3,1) so both co-applicant Employment tabs validate the same.
      if (
        applyCoApplicantEmploymentRules(
          shape,
          name,
          resolveCoApplicantIsEmployedField
        )
      ) {
        return;
      }
    });

    return Yup.object().shape(shape);
  }



  if (activeHorizontalIndex === 1 && activeVerticalIndex === 3) {
    // Duration of Education Gap (in months): optional. Bypass validation when
    // blank, but reject invalid input (negative numbers, decimals, or text).
    const educationGapField = fields.find(
      (field) => field.name === "educationGap"
    );
    if (educationGapField) {
      shape.educationGap = Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .typeError("Please enter a valid number of months for your education gap.")
        .integer("Please enter a valid number of months for your education gap.")
        .min(0, "Please enter a valid number of months for your education gap.")
        .notRequired();
    }

    // Academic Details: Only course fields
    fields
      .filter((field) => field.name.startsWith("course"))
      .forEach((field) => {
        // Academic Level (dropdown) - required
        if (field.name.includes("Type")) {
          shape[field.name] = Yup.mixed().nullable().test(
            "required-academic-level",
            "Please select an academic level.",
            (v) => v !== null && v !== undefined && String(v).trim() !== ""
          );
          return;
        }
        if (field.name.includes("Year")) {
          let yearSchema: NullableNumberSchema = Yup.number()
            .nullable()
            .typeError("Please select a valid passing year.")
            .integer("Year must be a whole number")
            .min(1950, "Please select a valid passing year.")
            .max(getCurrentYear(), "Cannot be a future year")
            .required("Please select a valid passing year.");

          if (field.name === "course2Year") {
            // Course 2 Year: Needs its own reference check
            yearSchema = yearSchema.when(
              ["course1Year", "course2Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course2Type] = values;
                
                // Skip validation if course type is Diploma
                if (course2Type === "Diploma") {
                  return schema;
                }
                
                const refData = getRefFor12th(course2Type, "course1Year");

                if ((course1Year || 0) > 0) {
                  const message = `Must be later than ${refData.messageSuffix}`;

                  return schema.moreThan(
                    refData.ref,
                    message
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          } else if (field.name === "course3Year") {
            // Course 3 Year: Needs both course type (for dynamic message) and flexibility logic
            yearSchema = yearSchema.when(
              ["course1Year", "course2Year", "course2Type", "course3Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course2Year, course2Type, course3Type] =
                  values;

                // Skip validation if course type is Diploma
                if (course3Type === "Diploma") {
                  return schema;
                }

                const refData = getRefFor12th(course3Type, "course2Year");

                // Determine which year to check against (10th or previous)
                const yearToCheckAgainst =
                  refData.ref.path === "course1Year"
                    ? course1Year
                    : course2Year;

                if ((yearToCheckAgainst || 0) > 0) {
                  let messageType = course2Type;
                  if (refData.messageSuffix === "10th Year") {
                    messageType = "10th";
                  }

                  const dynamicMessage = `Must be later than ${messageType} Year`;

                  return schema.moreThan(
                    refData.ref,
                    dynamicMessage
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          } else if (field.name === "course4Year") {
            // Course 4 Year: Needs flexibility logic
            yearSchema = yearSchema.when(
              ["course1Year", "course3Year", "course3Type", "course4Type"],
              (values: any[], schema: NullableNumberSchema) => {
                const [course1Year, course3Year, course3Type, course4Type] =
                  values;

                // Skip validation if course type is Diploma
                if (course4Type === "Diploma") {
                  return schema;
                }

                const refData = getRefFor12th(course4Type, "course3Year");
                const yearToCheckAgainst =
                  refData.ref.path === "course1Year"
                    ? course1Year
                    : course3Year;

                if ((yearToCheckAgainst || 0) > 0) {
                  let messageType = course3Type;
                  if (refData.messageSuffix === "10th Year") {
                    messageType = "10th";
                  }

                  const dynamicMessage = `Must be later than ${messageType} Year`;

                  return schema.moreThan(
                    refData.ref,
                    dynamicMessage
                  ) as NullableNumberSchema;
                }
                return schema;
              }
            );
          }

          shape[field.name] = yearSchema;
        } else if (field.name.includes("Percentage")) {
          // Co-Owner ownership percentage fields (coOwnerCollateral1Percentage,
          // coOwnerCollateral1_2OwnershipPercentage, ...) are unrelated to
          // academic marks percentage â€” they represent a share of ownership,
          // which is allowed to be 0 (the total across co-owners must add up
          // to 100, enforced separately in ReferenceCollateralDetails).
          const isCoOwnerOwnershipPercentage = /^coOwnerCollateral/i.test(
            field.name
          );
          const minPercentage = isCoOwnerOwnershipPercentage ? 0 : 40;

          let percentageSchema = Yup.number()
            .transform((value, originalValue) => {
              // keep original value as string for validation
              return originalValue === "" ? null : Number(originalValue);
            })
            .test(
              "no-invalid-chars",
              "Percentage cannot contain +, -, or e",
              (_, ctx) => {
                const input = String(ctx.originalValue || "");
                return !/[+\-eE]/.test(input);
              }
            )
            .test(
              "valid-number-format",
              "Enter a valid percentage (digits & max 2 decimals)",
              (_, ctx) => {
                const input = String(ctx.originalValue || "");
                if (!input) return true;
                return /^\d+(\.\d{1,2})?$/.test(input);
              }
            )
            .nullable()
            .typeError(`${field.label} must be a number`)
            .min(
              minPercentage,
              `${field.label} must be greater than or equal to ${minPercentage}`
            )
            .max(100, `${field.label} cannot exceed 100`);

          percentageSchema = percentageSchema.required(
            "Please enter the marks obtained."
          );
          shape[field.name] = percentageSchema;
        }
      });

    let finalSchema = Yup.object()
      .shape(shape)
      .test(
        "academic-years-unique",
        "Academic years cannot be the same.",
        function (values) {
          const yearsToCompare: number[] = [
            values.course1Year,
            values.course2Year,
            values.course3Year,
            values.course4Year,
          ]
            .map((val) => Number(val))
            .filter((val) => !isNaN(val) && val > 0);

          if (yearsToCompare.length < 2) {
            return true;
          }

          const uniqueYears = new Set(yearsToCompare);
          return uniqueYears.size === yearsToCompare.length;
        }
      );

    return finalSchema;
  } else if (activeHorizontalIndex === 0 && activeVerticalIndex === 2) {
    // "totalCost"
    shape.totalCost = Yup.number()
      .typeError("Total cost must be a number")
      .nullable()
      .optional()
      .min(1000000, "Total cost must be greater than 10,00,000")
      .max(50000000, "Total cost cannot exceed 5,00,00,000");

    // "loanAmount"
    shape.loanAmount = Yup.number()
      .typeError("Loan amount must be a number")
      .nullable()
      .optional()
      .min(1000000, "Loan amount must be greater than 10,00,000")
      .max(15000000, "Loan amount cannot exceed 1,50,00,000")
      .test(
        "loan-amount-not-greater-than-total-cost",
        "Loan amount cannot be greater than total cost",
        function (value) {
          const { totalCost } = this.parent;
          
          // if totalCost is missing or invalid according to its own rules, returning true here to HIDE the loanAmount error.
          if (!totalCost || totalCost < 1000000 || totalCost > 50000000) {
            return true; 
          }

          // only if totalCost is valid, perform the comparison
          if (!value) return true;
          return value <= totalCost;
        }
      );
  } else if (activeHorizontalIndex === 1 && activeVerticalIndex === 2) {
    fields.forEach((field) => {
      if (field.name?.toLowerCase() === "employmentlandmark") {
        shape[field.name] = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }
      if (field.name === "employmentCompanyAddress1") {
        shape[field.name] = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }
      if (field.name=== "employmentCompanyAddress2") {
        shape[field.name] = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }
      if (field.name === "employmentCompanyAddress3") {
        shape[field.name] = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }
      if (field.name === "employmentCompanyName") {
        shape[field.name] = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }
      //add validation here 
    });
  } else if (activeHorizontalIndex === 1 && activeVerticalIndex === 4) {
    // Entrance Exams: Conditional validation for core/language fields
    const isExamBlank = (v: any) =>
      v === null || v === undefined || String(v).trim() === "";

    fields.forEach((field) => {
      // "Have you appeared for any entrance exams?" - required selection
      if (field.name === "coreExamApplied") {
        shape[field.name] = Yup.string().test(
          "required-core-exam-applied",
          "Please select whether you have appeared for any entrance exams.",
          (v) => !isExamBlank(v)
        );
      } else if (/^coreExamName(_\d+)?$/.test(field.name)) {
        // Exam Name - required when applied = Yes
        // NOTE: Yup v1 passes the dependency value as an ARRAY to the callback.
        shape[field.name] = Yup.string().when(
          "coreExamApplied",
          ([coreExamApplied]: any[], schema: Yup.StringSchema) => {
            return coreExamApplied === "Yes"
              ? schema.test(
                  "required-core-exam-name",
                  "Please select or enter the exam name.",
                  (v) => !isExamBlank(v)
                )
              : schema.notRequired();
          }
        );
      } else if (/^coreScoreReceived(_\d+)?$/.test(field.name)) {
        // Received your results - required when applied = Yes
        shape[field.name] = Yup.string().when(
          "coreExamApplied",
          ([coreExamApplied]: any[], schema: Yup.StringSchema) => {
            return coreExamApplied === "Yes"
              ? schema.test(
                  "required-core-score-received",
                  "Please specify if you have received your results.",
                  (v) => !isExamBlank(v)
                )
              : schema.notRequired();
          }
        );
      } else if (/^coreTotalMarks(_\d+)?$/.test(field.name)) {
        // Total Marks - required when the exam is applied (= Yes) AND the results
        // have been received. Answering "Received Your Results?" with No hides the
        // marks inputs (an expected-date field is asked for instead), so they must
        // not be required then.
        {
          const suffixMatch = field.name.match(/(_\d+)$/);
          const suffix = suffixMatch ? suffixMatch[1] : "";
          const scoreReceivedField = `coreScoreReceived${suffix}`;

          shape[field.name] = Yup.string().when(
            ["coreExamApplied", scoreReceivedField],
            ([coreExamApplied, scoreReceived]: any[], schema: Yup.StringSchema) => {
              return coreExamApplied === "Yes" && scoreReceived !== "No"
                ? schema.test(
                    "required-core-total-marks",
                    "Please enter the total marks.",
                    (v) => !isExamBlank(v)
                  )
                : schema.notRequired();
            }
          );
        }
      } else if (/^coreExamScore(_\d+)?$/.test(field.name)) {
        // Marks Obtained - required whenever the exam is applied (= Yes).
        // Also validate score not greater than total marks / not less than min.
        shape[field.name] = Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value
          )
          .typeError("Please enter the marks obtained.")
          .test(
            "required-core-exam-score",
            "Please enter the marks obtained.",
            function (value) {
              const applied = (this.parent as any)?.coreExamApplied;
              if (applied !== "Yes") return true;
              // "Received Your Results?" = No hides the marks inputs (an expected
              // date is asked for instead), so nothing is required here.
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";
              const scoreReceived = (this.parent as any)?.[
                `coreScoreReceived${suffix}`
              ];
              if (scoreReceived === "No") return true;
              return value !== null && value !== undefined;
            }
          )
          .test(
            "not-greater-than-total-marks",
            `${field.label || field.name} cannot be greater than total marks`,
            function (value) {
              // Find corresponding total marks field
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";
              const totalMarksField = `coreTotalMarks${suffix}`;
              const totalMarks = this.parent[totalMarksField];
              if (value == null || totalMarks == null || totalMarks === "")
                return true;
              return Number(value) <= Number(totalMarks);
            }
          )
          .test(
            "not-less-than-min-marks",
            "", // keep message empty, we will generate it dynamically
            function (this: Yup.TestContext, value) {
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";

              const totalMarksField = `coreTotalMarks${suffix}`;
              const totalMarks = (this.parent as any)[totalMarksField];

              if (value == null || totalMarks == null || totalMarks === "")
                return true;

              const minValue = Number(minMarks?.[totalMarks?.toString()]);

              //  If invalid â†’ return custom error with dynamic value
              if (Number(value) < minValue) {
                return this.createError({
                  message: `${field.label || field.name} cannot be less than ${minValue}`,
                });
              }

              return true;
            }
          );
      } else if (field.name === "languageExamApplied") {
        // "Have you appeared for any language proficiency exam?" - required
        shape[field.name] = Yup.string().test(
          "required-language-exam-applied",
          "Please select whether you have appeared for any language proficiency exam.",
          (v) => !isExamBlank(v)
        );
      } else if (/^languageExamName(_\d+)?$/.test(field.name)) {
        // Exam Name - required when applied = Yes
        // NOTE: Yup v1 passes the dependency value as an ARRAY to the callback.
        shape[field.name] = Yup.string().when(
          "languageExamApplied",
          ([languageExamApplied]: any[], schema: Yup.StringSchema) => {
            return languageExamApplied === "Yes"
              ? schema.test(
                  "required-language-exam-name",
                  "Please select the language exam name.",
                  (v) => !isExamBlank(v)
                )
              : schema.notRequired();
          }
        );
      } else if (/^languageScoreReceived(_\d+)?$/.test(field.name)) {
        // Received your results - required when applied = Yes
        shape[field.name] = Yup.string().when(
          "languageExamApplied",
          ([languageExamApplied]: any[], schema: Yup.StringSchema) => {
            return languageExamApplied === "Yes"
              ? schema.test(
                  "required-language-score-received",
                  "Please select Received Your Results?",
                  (v) => !isExamBlank(v)
                )
              : schema.notRequired();
          }
        );
      } else if (/^languageTotalMarks(_\d+)?$/.test(field.name)) {
        // Total Marks - required when the exam is applied (= Yes) AND the results
        // have been received. Answering "Received Your Results?" with No hides the
        // marks inputs (an expected-date field is asked for instead), so they must
        // not be required then.
        {
          const suffixMatch = field.name.match(/(_\d+)$/);
          const suffix = suffixMatch ? suffixMatch[1] : "";
          const scoreReceivedField = `languageScoreReceived${suffix}`;

          shape[field.name] = Yup.string().when(
            ["languageExamApplied", scoreReceivedField],
            ([languageExamApplied, scoreReceived]: any[], schema: Yup.StringSchema) => {
              return languageExamApplied === "Yes" && scoreReceived !== "No"
                ? schema.test(
                    "required-language-total-marks",
                    "Please enter the total marks.",
                    (v) => !isExamBlank(v)
                  )
                : schema.notRequired();
            }
          );
        }
      } else if (/^languageExamScore(_\d+)?$/.test(field.name)) {
        // Marks Obtained - required whenever the exam is applied (= Yes).
        // Also validate score not greater than total marks / not less than min.
        shape[field.name] = Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value
          )
          .typeError("Please enter the marks obtained.")
          .test(
            "required-language-exam-score",
            "Please enter the marks obtained.",
            function (value) {
              const applied = (this.parent as any)?.languageExamApplied;
              if (applied !== "Yes") return true;
              // "Received Your Results?" = No hides the marks inputs (an expected
              // date is asked for instead), so nothing is required here.
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";
              const scoreReceived = (this.parent as any)?.[
                `languageScoreReceived${suffix}`
              ];
              if (scoreReceived === "No") return true;
              return value !== null && value !== undefined;
            }
          )
          .test(
            "not-greater-than-total-marks",
            `${field.label || field.name} cannot be greater than total marks`,
            function (value) {
              // Find corresponding total marks field
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";
              const totalMarksField = `languageTotalMarks${suffix}`;
              const totalMarks = this.parent[totalMarksField];
              if (value == null || totalMarks == null || totalMarks === "")
                return true;
              return Number(value) <= Number(totalMarks);
            }
          )
          .test(
            "not-less-than-min-marks",
            "", // keep message empty, we will generate it dynamically
            function (this: Yup.TestContext, value) {
              const suffixMatch = this.path.match(/(_\d+)$/);
              const suffix = suffixMatch ? suffixMatch[1] : "";

              const totalMarksField = `languageTotalMarks${suffix}`;
              const totalMarks = (this.parent as any)[totalMarksField];

              if (value == null || totalMarks == null || totalMarks === "")
                return true;

              // Language exam marks can never be below 1 — covers exams whose
              // total isn't in minMarks (NaN) or maps to 0 (e.g. TOEFL 120).
              const minValue = Math.max(
                1,
                Number(minMarks?.[totalMarks?.toString()]) || 0
              );

              //  If invalid â†’ return custom error with dynamic value
              if (Number(value) < minValue) {
                return this.createError({
                  message: `${field.label || field.name} cannot be less than ${minValue}`,
                });
              }

              return true;
            }
          );
      } else if (/^coreScoreExpectedOn(_\d+)?$/.test(field.name)) {
        // Expected Date - required when applied = Yes and results received = No
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[1] : "";
        const scoreReceivedField = `coreScoreReceived${suffix}`;
        shape[field.name] = Yup.string()
          .nullable()
          .when(
            ["coreExamApplied", scoreReceivedField],
            ([coreExamApplied, scoreReceived]: any[], schema: any) => {
              return coreExamApplied === "Yes" && scoreReceived === "No"
                ? schema.test(
                    "required-core-expected-date",
                    "Please enter the date",
                    (v: any) => !isExamBlank(v)
                  )
                : schema.notRequired();
            }
          );
      } else if (/^languageScoreExpectedOn(_\d+)?$/.test(field.name)) {
        // Expected Date - required when applied = Yes and results received = No
        const suffixMatch = field.name.match(/(_\d+)$/);
        const suffix = suffixMatch ? suffixMatch[1] : "";
        const scoreReceivedField = `languageScoreReceived${suffix}`;
        shape[field.name] = Yup.string()
          .nullable()
          .when(
            ["languageExamApplied", scoreReceivedField],
            ([languageExamApplied, scoreReceived]: any[], schema: any) => {
              return languageExamApplied === "Yes" && scoreReceived === "No"
                ? schema.test(
                    "required-language-expected-date",
                    "Please enter the date",
                    (v: any) => !isExamBlank(v)
                  )
                : schema.notRequired();
            }
          );
      }
    });
  } else {
    // Default: all required fields (modified)
    fields.forEach((field) => {
      // Skip strict validation for collateral autocomplete fields that store plain strings
      if (
        /^collateral\d*addressType$/i.test(field.name) ||
        /^collateral\d*propertyName$/i.test(field.name) ||
        /^collateral\d*propertypeName$/i.test(field.name) ||
        /^collateral\d*propertyType$/i.test(field.name) ||
        /^collateral\d*propertySubTypeName$/i.test(field.name) ||
        /^collateral\d*propertySubType$/i.test(field.name)
      ) {
        shape[field.name] = Yup.mixed().nullable().optional();
        return;
      }
      if (
        pageType === "SUMMARY" &&
        activeHorizontalIndex === 0 &&
        activeVerticalIndex === 0 &&
        (field.name === "terms_conditions" ||
          field.name === "privacy_policy")
      ) {
        shape[field.name] = Yup.boolean().optional();
        return;
      }
      if (
        pageType === "SUMMARY" &&
        activeHorizontalIndex === 0 &&
        activeVerticalIndex === 0 &&
        field.name === "applicantType"
      ) {
        shape[field.name] = Yup.string()
          .test(
            "applicant-type-summary",
            "Please select who is applying",
            (value) => value != null && String(value).trim() !== ""
          )
          .nullable()
          .optional();
        return;
      }
      // Special handling for address year fields
      if (
        field.name === "permanentYearsInCurrentAddress" ||
        field.name === "permanentYearsInCurrentCity" ||
        field.name === "currentYearsInCurrentAddress" ||
        field.name === "currentYearsInCurrentCity" ||
        /^addressCoApplicant\d+(permanent|current)YearsInCurrent(Address|City)$/.test(field.name)
      ) {
        shape[field.name] = Yup.number()
          .typeError(`${field.label || field.name} must be a number`)
          .nullable()
          .optional()
          .transform((value, originalValue) => (originalValue === "" ? null : value))
          .min(0, "Years must be at least 0")
          .max(150, "Years cannot exceed 150");

        return;
      }
      // Special handling for LoanDetailsLoanAmount
      if (field.name === "LoanDetailsLoanAmount") {
        shape[field.name] = Yup.number()
          .typeError("Loan amount must be a number")
          .nullable()
          .optional()
          .min(1000000, "Loan amount must be at least 10,00,000")
          .max(15000000, "Loan amount cannot exceed 1,50,00,000");
        return;
      }
      // Special handling for collateralType and collateralAmount
      if (
        // field.name === "collateralType" ||
        field.name === "collateralAmount"
      ) {
        shape[field.name] = Yup.string().when("collateral", {
          is: (val: any) => val === "Yes",
          then: (schema: any) =>
            // schema.required(`${field.label} is required`), // COMMENTED
            schema
              .notRequired() // Use notRequired for "then" if required is commented
              .test(
                "max-value",
                "Collateral amount cannot exceed 1.5 Crore (15,000,000)",
                (value: any) => {
                  const numValue = Number(value);
                  if (isNaN(numValue)) return true;
                  return numValue <= 15000000;
                }
              ),
          otherwise: (schema: any) => schema.notRequired(),
        });
        return;
      }
      // PAN validation: required and regex (main applicant)
      if (field.name === "pan") {
        shape[field.name] = (
          field.required
            ? Yup.string()
                // .required("PAN is required") // COMMENTED
                .nullable() // Added .nullable() when .required() is removed
            : Yup.string()
        )
          .transform((value) =>
            value ? value.trim().toUpperCase().slice(0, 10) : ""
          )
          .when([], {
            is: (val: string) =>
              val !== undefined && val !== null && val !== "",
            then: (schema) =>
              schema.length(10, "PAN must be exactly 10 characters"),
            otherwise: (schema) => schema,
          })
          .test("pan-format", "PAN must be in format AAAAA9999A", (value) => {
            if (!value) return true; // allow empty, required handled above if needed
            if (value.length !== 10) return true; // Only check format if exactly 10 chars
            return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value) ? true : false;
          });
        return;
      }
      // PAN validation: required and regex (co-applicant PAN fields)
      if (/^basicCoApplicant\d+PanNumber$/.test(field.name)) {
        shape[field.name] = (
          field.required
            ? Yup.string()
                // .required("PAN is required") // COMMENTED
                .nullable() // Added .nullable() when .required() is removed
            : Yup.string()
        )
          .transform((value) =>
            value ? value.trim().toUpperCase().slice(0, 10) : ""
          )
          .when([], {
            is: (val: string) =>
              val !== undefined && val !== null && val !== "",
            then: (schema) =>
              schema.length(10, "PAN must be exactly 10 characters"),
            otherwise: (schema) => schema,
          })
          .test("pan-format", "PAN must be in format AAAAA9999A", (value) => {
            if (!value) return true; // allow empty, required handled above if needed
            if (value.length !== 10) return true; // Only check format if exactly 10 chars
            return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value) ? true : false;
          });
        return;
      }

      if (
        (field.name.startsWith("addressCoApplicant") &&
          field.name.endsWith("permanentAddressLine1")) ||
        field.name.endsWith("currentAddressLine1") ||
        field.name.endsWith("permanentAddressLine2") ||
        field.name.endsWith("currentAddressLine2") ||
        field.name.endsWith("permanentAddressLine3") ||
        field.name.endsWith("currentAddressLine3")
      ) {
        shape[field.name] = Yup.string().max(
          50,
          "Maximum 50 characters allowed."
        );
        return;
      }

      if (
        field.name == "permanentAddressLine1" ||
        field.name == "currentAddressLine1" ||
        field.name == "permanentAddressLine2" ||
        field.name == "currentAddressLine2" ||
        field.name == "permanentAddressLine3" ||
        field.name == "currentAddressLine3"
      ) {
        shape[field.name] = Yup.string().max(
          50,
          "Maximum 50 characters allowed."
        );
        return;
      }
      if (
        field.name == "employmentCompanyAddress1" ||
        field.name == "employmentCompanyAddress2" ||
        field.name == "employmentCompanyAddress3"
      ) {
        shape[field.name] = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      // Validation for employment company name - 2 to 50 characters
      if (field.name === "employmentCompanyName") {
        shape[field.name] = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      // Validation for employment designation - 2 to 50 characters
      if (field.name === "employmentDesignation") {
        shape[field.name] = Yup.string()
          .min(1, "Minimum 1 characters required.")
          .nullable()
          .optional();
        return;
      }

      // Validation for employmentYear - must be between 0 and 60
      if (field.name === "employmentYear") {
        shape[field.name] = Yup.number()
          .typeError("Must be a valid number")
          .min(0, "Years cannot be negative")
          .max(60, "Employment year cannot be greater than 60")
          .integer("Must be a whole number")
          .nullable()
          .optional();
        return;
      }

      // Validation for employmentMonth - must be between 0 and 12
      if (field.name === "employmentMonth") {
        shape[field.name] = Yup.number()
          .typeError("Must be a valid number")
          .min(0, "Months cannot be negative")
          .max(12, "Employment month cannot be greater than 12")
          .integer("Must be a whole number")
          .nullable()
          .optional();
        return;
      }

      

      // Monthly Obligation - max value 20 (DIY / dynamic form validation)
      if (
        field.name === "employmentMonthlyObligation"
      ) {
        shape[field.name] = Yup.number()
          .typeError("Monthly Obligation must be a number")
          .min(0, "Monthly Obligation cannot be negative")
          .max(20, "Monthly Obligation cannot exceed 20")
          .nullable()
          .optional();
        // return;
      }

      // Validation for employment company city - 2 to 50 characters
      if (field.name === "employmentCompanyCity") {
        shape[field.name] = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      // Validation for employment company state - 2 to 50 characters
      if (field.name === "employmentCompanyState") {
        shape[field.name] = Yup.string()
          .min(2, "Minimum 2 characters required.")
          .max(50, "Maximum 50 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      // Validation for firstName and lastName - max 26 characters
      if (
        field.name === "firstName" ||
        field.name === "lastName" ||
        field.name === "studentDetailsFirstName" ||
        field.name === "studentDetailsLastName" ||
        field.name === "fatherFirstName" ||
        field.name === "fatherLastName" ||
        field.name === "motherFirstName" ||
        field.name === "motherLastName" ||
        /^basicCoApplicant\d+FirstName$/.test(field.name) ||
        /^basicCoApplicant\d+LastName$/.test(field.name)
      ) {
        shape[field.name] = Yup.string()
          .max(26, "Maximum 26 characters allowed.")
          .nullable()
          .optional()
          .test(
            "not-only-spaces",
            "Spaces are not allowed.",
            (value) => {
              if (value == null || value === "") return true; // allow empty (since optional)
              return value.trim().length > 0; // reject "   "
            }
          );
        return;
      }

      // Validation for fatherName and motherName - max 150 characters
      if (
        field.name === "fatherName" ||
        field.name === "motherName" ||
        /^basicCoApplicant\d+FatherName$/.test(field.name) ||
        /^basicCoApplicant\d+MotherName$/.test(field.name)
      ) {
        shape[field.name] = Yup.string()
          .max(150, "Maximum 150 characters allowed.")
          .nullable()
          .optional();
        return;
      }

      if (field.name === "applicantDependents") {
        shape[field.name] = Yup.number()
          .typeError("Number of dependents must be a number")
          .integer("Number of dependents must be a whole number")
          .min(1, "Number of dependents must be at least 1")
          .max(10, "You can enter a maximum of 10 dependents. Please adjust the number to proceed.")
          .nullable()
          .optional();
        return;
      }
      if (
        /^basicCoApplicant\d+(RelationValue|OtherRelationWithStudent)$/.test(
          field.name,
        )
      ) {
        const relationFieldName = field.name.replace(
          /(RelationValue|OtherRelationWithStudent)$/,
          "RelationWithStudent"
        );
        shape[field.name] = Yup.string().when(relationFieldName, {
          is: "OTHERS",
          then: (schema) =>
            schema.trim().required("Please specify your relationship with student"),
          otherwise: (schema) => schema.nullable().optional(),
        });
        return;
      }
      if (field.name === "loanRequestedRoi") {
        shape[field.name] = Yup.number()
          .typeError("Requested ROI must be a number")
          .max(20, "ROI limit exceeded. Value can be 20% or less.")
          .nullable()
          .optional();
        return;
      }

      // Required Loan Amount (Applicant Additional Details): the generic
      // `field.required` numeric rule below has its `.required()` commented
      // out, so a blank value never errored; 0 also needs its own check
      // since a numeric `.required()` alone treats 0 as a valid value.
      if (field.name === "loanLoanAmount") {
        shape[field.name] = Yup.number()
          .typeError("Please enter the required loan amount.")
          .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? undefined : value
          )
          .required("Please enter the required loan amount.")
          .moreThan(0, "Loan amount must be greater than 0.");
        return;
      }

      // Co-applicant employment designation: only required when the
      // co-applicant is actually employed and their employment type isn't
      // Farmer/Others (those have no designation to give) â€” without this,
      // the generic `field.required` dropdown rule below always requires it.
      if (/^employmentCoApplicant\d+designation$/i.test(field.name)) {
        const coAppNum =
          field.name.match(/^employmentCoApplicant(\d+)designation$/i)?.[1] ||
          "";
        const isEmployedFieldName = `employmentCoApplicant${coAppNum}employmentisEmployed`;
        const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
        shape[field.name] = Yup.string().when(
          [isEmployedFieldName, employmentTypeFieldName],
          {
            is: (isEmployed: any, employmentType: any) =>
              String(isEmployed).toLowerCase() === "yes" &&
              !["Farmer", "Others"].includes(String(employmentType || "")),
            then: (schema) =>
              schema.notOneOf([""], "Please select a designation."),
            otherwise: (schema) => schema.nullable().optional(),
          },
        );
        return;
      }

      // Co-applicant company address (DIY & Summary): mandatory once
      // employment type is Salaried/SEP/SENP, matching withMandatoryCompanyAddress
      // in CoApplicantDetailsSection.tsx / CoApplicantAdditionalDetails.tsx /
      // SingleCoApplicantAdditionalDetails.tsx, which flip these fields'
      // `required` flag on for the same condition but rely on this schema to
      // actually enforce it.
      const companyAddressMatch = field.name.match(
        /^employmentCoApplicant(\d+)(companyAddress1|companyAddress2|companyAddress3|landmark|pincode|city|state|country)$/i,
      );
      if (companyAddressMatch) {
        const coAppNum = companyAddressMatch[1];
        const subField = companyAddressMatch[2].toLowerCase();
        const isEmployedFieldName = `employmentCoApplicant${coAppNum}employmentisEmployed`;
        const employmentTypeFieldName = `employmentCoApplicant${coAppNum}employmentType`;
        const isCompanyAddressRequired = (isEmployed: any, employmentType: any) =>
          String(isEmployed).toLowerCase() === "yes" &&
          ["Salaried", "SEP", "SENP"].includes(String(employmentType || ""));

        // Address Line 2/3 stay optional even once the rest becomes mandatory.
        if (subField === "companyaddress2" || subField === "companyaddress3") {
          shape[field.name] = Yup.string()
            .max(50, "Maximum 50 characters allowed.")
            .nullable()
            .optional();
          return;
        }

        if (subField === "pincode") {
          shape[field.name] = Yup.string().when(
            [isEmployedFieldName, employmentTypeFieldName],
            {
              is: isCompanyAddressRequired,
              then: (schema) =>
                schema
                  .matches(/^[0-9]{6}$/, "Please enter a valid 6-digit pincode.")
                  .required("Please enter a valid 6-digit pincode."),
              otherwise: (schema) => schema.nullable().optional(),
            },
          );
          return;
        }

        shape[field.name] = Yup.string()
          .max(50, "Maximum 50 characters allowed.")
          .when([isEmployedFieldName, employmentTypeFieldName], {
            is: isCompanyAddressRequired,
            then: (schema) =>
              schema.trim().required(`${field.label || field.name} is required.`),
            otherwise: (schema) => schema.nullable().optional(),
          });
        return;
      }

      if (field.required) {
        switch (field.type) {
          case "number":
            shape[field.name] = Yup.number()
              .typeError(`${field.label} must be a number`)
              // .required(`${field.label} is required`) // COMMENTED
              .nullable() // Add nullable when required is removed
              .optional(); // Add optional when required is removed
            break;
          case "phone":
            shape[field.name] = Yup.string()
              .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
          case "email":
            shape[field.name] = Yup.string()
              .email("Please enter a valid email address")
              .max(40, "Mail ID must not exceed 40 characters")
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
          case "date":
            shape[field.name] = Yup.date()
              .typeError(`Please enter a valid ${field.label}`)
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
          case "dropdown":
            shape[field.name] = Yup.string()
              .notOneOf([""], `Please select a ${field.label}`)
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
          case "autocomplete":
            shape[field.name] = Yup.mixed()
              .test(
                "autocomplete-required",
                `Please select a ${field.label}`,
                (value) => value !== null && value !== undefined && value !== ""
              )
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();

                  if (notInList?.includes(field.label)) {
                      // When "Not in list" is selected, value is required
                      shape[field.name] = Yup.mixed()
                        .nullable()
                        .test(
                          "not-in-list-required",
                          `When Not In List is selected, please enter the ${field.label}`,
                          (value: any) => {
                            const text =
                              value == null || value === ""
                                ? ""
                                : typeof value === "string"
                                  ? value
                                  : value?.label ?? value?.value ?? "";
                            return (text ?? "").toString().trim().length > 0;
                          }
                        )
                        .test(
                          "has-alphanumeric",
                          "Only letters, numbers and ! @ # . / + are allowed",
                          (value: any) => {
                            if (!value) return true;

                            const text =
                              typeof value === "string"
                                ? value
                                : value?.label || value?.value;

                            if (!text) return true;

                            return /^[a-zA-Z0-9 !@#./+]+$/.test(text);
                          }
                        );
                      return;
                    }
            break;
          // case "autocomplete":
              shape[field.name] = Yup.mixed()
                .nullable()
                .test(
                  "autocomplete-required",
                  `Please select a ${field.label}`,
                  (value) =>
                    value !== null &&
                    value !== undefined &&
                    value !== ""
                )
                .test(
                  "has-alphanumeric",
                  "Only letters, numbers and ! @ # . / + are allowed",
                  (value: any) => {
                    if (!value) return true; // required test handles empty

                    // if autocomplete returns object
                    const text =
                      typeof value === "string"
                        ? value
                        : value?.label || value?.value;

                    if (!text) return true;

                   return /^[a-zA-Z0-9 !@#./+]+$/.test(text);
                  }
                )
                .optional();
              break;
          case "radio":
            shape[field.name] = Yup.string()
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
          case "checkbox":
            shape[field.name] = Yup.boolean()
              .oneOf([true], `${field.label} must be checked`)
              // .required(`${field.label} is required`); // COMMENTED
              .nullable()
              .optional();
            break;
          default:
            shape[field.name] = Yup.string()
              // .required(`${field.label} is required`) // COMMENTED
              .nullable()
              .optional();
            break;
        }
      } else {
        switch (field.type) {
          case "number":
            shape[field.name] = Yup.number()
              .typeError(`${field.label} must be a number`)
              .nullable()
              .optional();
            break;
          case "phone":
            shape[field.name] = Yup.string()
              .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
              .nullable()
              .optional();
            break;
          case "email":
            shape[field.name] = Yup.string()
              .email("Please enter a valid email address")
              .max(40, "Mail ID must not exceed 40 characters")
              .nullable()
              .optional();
            break;
          case "date":
            shape[field.name] = Yup.date()
              .typeError(`Please enter a valid ${field.label}`)
              .nullable()
              .optional();
            break;
          case "dropdown":
            shape[field.name] = Yup.string().nullable().optional();
            break;
          case "radio":
            shape[field.name] = Yup.string().nullable().optional();
            break;
          default:
            shape[field.name] = Yup.string().nullable().optional();
            break;
        }
      }
    });
  }

  // Co-Applicant Additional Details (3,1) renders the same co-applicant Basic
  // Details tab as Loan Details (1,5), but has no branch of its own â€” it reaches
  // the generic loop above, where every `.required()` is commented out (its
  // co-applicant PAN rule, for instance, only checks format and lets blank
  // through). The tab therefore had no required validation at all.
  //
  // Applied AFTER that loop on purpose: these rules must win over the weaker
  // generic ones for the same field names. Everything the loop contributes for
  // other co-applicant fields (address Years in Current max-150, name character
  // rules, employment designation, ...) is left untouched.
  // The loan application overview page (SUMMARY) edits these same tabs as
  // (4,0) / (4,1), and must show the same messages as the extended journey.
  const isSummaryAdditionalStep = (verticalIndex: number) =>
    pageType === "SUMMARY" &&
    activeHorizontalIndex === 4 &&
    activeVerticalIndex === verticalIndex;

  if (
    (activeHorizontalIndex === 3 && activeVerticalIndex === 1) ||
    isSummaryAdditionalStep(1)
  ) {
    fields.forEach((field) => {
      const name = String(field?.name || "");
      if (applyCoApplicantBasicDetailsRules(shape, name)) return;
      if (applyCoApplicantAddressRules(shape, name)) return;
      applyCoApplicantEmploymentRules(
        shape,
        name,
        resolveCoApplicantIsEmployedField
      );
    });
  }

  if (
    (activeHorizontalIndex === 3 && activeVerticalIndex === 0) ||
    isSummaryAdditionalStep(0)
  ) {
    const isBlank = isBlankCoApplicantValue;

    // Adds required on top of whatever rule the generic loop already built, so
    // existing range/type checks for the field survive.
    const appendRequired = (name: string, message: string, fallback: any) => {
      const existing = shape[name];
      shape[name] =
        existing && typeof existing.required === "function"
          ? existing.required(message)
          : fallback.required(message);
    };

    const requireSelection = (name: string, testName: string, message: string) => {
      shape[name] = Yup.mixed().nullable().test(testName, message, (v) => !isBlank(v));
    };

    fields.forEach((field) => {
      const name = String(field?.name || "");
      if (!name) return;
      const haystack = String(field?.label || "") + name;

      // Number of Dependents
      if (/dependents?/i.test(haystack)) {
        appendRequired(
          name,
          "Please enter the number of dependents.",
          Yup.number()
            .transform(blankToUndefined)
            .typeError("Please enter a valid number of dependents.")
            .min(0, "Number of dependents cannot be negative")
        );
        return;
      }
      // Nationality ("naionality" is a known misspelling in some templates)
      if (/nationality|naionality/i.test(haystack)) {
        requireSelection(
          name,
          "required-additional-nationality",
          "Please select Nationality"
        );
        return;
      }
      // Marital Status
      if (/marital/i.test(haystack)) {
        requireSelection(
          name,
          "required-additional-marital-status",
          "Please select Marital Status"
        );
        return;
      }
      // City of Birth — checked before Country of Birth so a label containing
      // both words resolves to the city field, matching the Continue handler.
      if (/city.*birth|birth.*city/i.test(haystack)) {
        requireSelection(
          name,
          "required-additional-city-of-birth",
          "Please select City of Birth"
        );
        return;
      }
      // Country of Birth
      if (/country.*birth|birth.*country/i.test(haystack)) {
        requireSelection(
          name,
          "required-additional-country-of-birth",
          "Please select Country of Birth"
        );
        return;
      }
      // Required Loan Amount (in ₹)
      if (/required\s*loan\s*amount|loan\s*amount/i.test(haystack)) {
        appendRequired(
          name,
          "Please enter the required loan amount.",
          Yup.number()
            .transform(blankToUndefined)
            .typeError("Please enter a valid loan amount.")
        );
        return;
      }
      // Expected Date Of Disbursement. Required only — the picker already blocks
      // past dates and reports that itself through an imperative error message.
      if (/expected\s*date\s*of\s*disbursement|disbursement/i.test(haystack)) {
        requireSelection(
          name,
          "required-additional-disbursement-date",
          "Please select the expected date of disbursement."
        );
        return;
      }
    });
  }

  // Fund & Expense (3,2). Another step with no branch of its own that reaches the
  // generic loop below, where every `.required()` is commented out — so none of
  // these amounts were mandatory.
  //
  // All eight are required. "Undecided" applications, which may leave them
  // blank, are handled by the Continue handler zero-filling them before it
  // validates — see FUND_EXPENSE_AMOUNT_FIELDS in DynamicForm.
  if (activeHorizontalIndex === 3 && activeVerticalIndex === 2) {
    // Names match FUND_EXPENSE_LOAN_AMOUNT_FIELD + FUND_EXPENSE_CONDITIONAL_FIELDS
    // in utils/helpers.ts. Held locally because each needs its own message.
    const fundExpenseMessages: Record<string, string> = {
      expenseTuitionFees: "Please enter the tuition fees.",
      expenseLivingExpenses: "Please enter the living expenses.",
      expenseTravelExpenses: "Please enter the travel expenses.",
      expenseOtherExpenses: "Please enter the other expenses.",
      fundRequestedLoanAmount: "Please enter the requested loan amount.",
      fundOwnSources: "Please enter the own sources amount.",
      fundScholarshipAmount: "Please enter the scholarship amount.",
      fundOtherIncome: "Please enter the other income.",
    };

    const toScalar = (raw: any) =>
      raw && typeof raw === "object"
        ? (raw as any).value ?? (raw as any).label ?? raw
        : raw;

    // These inputs carry Indian-comma formatting ("1,00,000"), so the blank check
    // strips separators instead of casting to a number — mirrors
    // isFundExpenseValueFilled in utils/helpers.ts.
    const isAmountFilled = (v: any) => {
      const scalar = toScalar(v);
      if (scalar === null || scalar === undefined) return false;
      return String(scalar).replace(/,/g, "").trim() !== "";
    };

    fields.forEach((field) => {
      const name = String(field?.name || "");
      const message = fundExpenseMessages[name];
      if (!message) return;

      shape[name] = Yup.mixed().nullable().test(`required-${name}`, message, (value) =>
        isAmountFilled(value)
      );
    });
  }

  return Yup.object().shape(shape);
};

// Every email field in the journey and Loan Summary must be a valid address
// without "+" or "-". Empty values are left to each field's own required rule.
const applyEmailFormatValidation = (schema: any) => {
  const emailRules: Record<string, any> = {};
  Object.keys(schema?.fields || {}).forEach((name) => {
    if (!isEmailFieldName(name)) return;
    emailRules[name] = schema.fields[name].test(
      "journey-email-format",
      JOURNEY_EMAIL_ERROR,
      (value: any) =>
        value === undefined ||
        value === null ||
        String(value).trim() === "" ||
        isValidJourneyEmail(value)
    );
  });
  return Object.keys(emailRules).length ? schema.shape(emailRules) : schema;
};

// Every pincode field (applicant, co-applicant, reference, collateral, employment...)
// must be exactly 6 digits when filled; empty values are left to the field's own
// required rule.
const PINCODE_FIELD_REGEX = /pin\s*code$/i;

const applyPincodeLengthValidation = (schema: any) => {
  const schemaFields = schema?.fields;
  if (!schemaFields || typeof schema?.shape !== "function") return schema;

  const pincodeShape: Record<string, any> = {};
  Object.keys(schemaFields).forEach((name) => {
    const fieldSchema = schemaFields[name];
    if (!PINCODE_FIELD_REGEX.test(name) || typeof fieldSchema?.test !== "function") {
      return;
    }
    pincodeShape[name] = fieldSchema.test(
      "pincode-length",
      "Pincode must be 6 digits",
      (value: any) => {
        if (value === undefined || value === null || String(value).trim() === "") {
          return true;
        }
        return /^\d{6}$/.test(String(value).trim());
      },
    );
  });

  return Object.keys(pincodeShape).length > 0
    ? schema.shape(pincodeShape)
    : schema;
};

export const getValidationSchema = (
  fields: any[],
  activeHorizontalIndex?: number,
  activeVerticalIndex?: number,
  notInList?: any,
  pageType?: any
) =>
  applyEmailFormatValidation(
    applyPincodeLengthValidation(
      buildValidationSchema(
        fields,
        activeHorizontalIndex,
        activeVerticalIndex,
        notInList,
        pageType,
      ),
    ),
  );
